import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {ProductImage as ProductImageType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_images.types';
import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useTranslation} from '@aboutyou/i18n';
import {ProductTrackingData} from '@aboutyou/tracking';
import {
  COLORS,
  FluidGrid,
  Flyout,
  FONT_SIZES,
  FONT_WEIGHTS,
  ProductImage,
  Text,
} from '@aboutyou/ui-components';
import React, {useState} from 'react';
import styled from 'styled-components';
import {AddToBasketSuccessFlyoutWithRecos} from '../../../../../features/AddToBasketSuccessFlyout/AddToBasketSuccessFlyoutWithRecos';
import {useSuccessFlyoutOpenedState} from '../../../../../features/AddToBasketSuccessFlyout/hooks/useSuccessFlyoutOpenedState';
import {FullHeightProductTile} from '../../../../../features/ProductTile/FullHeightProductTile';
import {AddToBasketPayload} from '../../../../../features/ProductTile/types';
import {AddToBasketFlyout} from '../../../../../features/SizeSelectionFlyout/AddToBasketFlyout';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {useOutfitCollageFlyoutOpenedState} from './utils/useOutfitCollageFlyoutOpenedState';

interface OutfitCollageFlyoutProps {
  ['data-testid']?: string;
  headline: string;
  headerContent: {
    image: ProductImageType;
    headline: string;
    subline?: string;
  };
  openedStateKey: string;
  products: ProductTileV2[];
  sizeSelectionOpenedStateKey: SizeSelectionOpenedStateKey;
  trackingData?: ProductTrackingData;
  trackProductClick?: VoidFunction;
  outfitPromotionId?: string;
}

export const OutfitCollageFlyout: React.FC<OutfitCollageFlyoutProps> = ({
  'data-testid': dataTestId,
  headline,
  headerContent,
  openedStateKey,
  products,
  sizeSelectionOpenedStateKey,
  trackingData,
  trackProductClick,
  outfitPromotionId,
}) => {
  const {t} = useTranslation();

  const {cancel, isOpen} = useOutfitCollageFlyoutOpenedState(openedStateKey);
  const [selectedProduct, setSelectedProduct] = useState<{
    product: AddToBasketPayload['product'];
    brandName: string;
  }>();
  const [selectedCouponPromotionId, setSelectedCouponPromotionId] =
    useState<string>();

  const [productId, setProductId] = useState<number>();
  const [statusSuccessSections, setStatusSuccessSections] = useState<
    AddItemResponseStatus_Success_Section[]
  >([]);
  const [freeDeliverySection, setFreeDeliverySection] =
    useState<FreeDeliverySection>();

  const {
    open: openSizeSelection,
    isOpen: addToBasket,
    cancel: cancelSizeSelection,
  } = useSizeSelectionOpenedState(sizeSelectionOpenedStateKey);

  const closeProductOutfitFlyout = () => {
    setSelectedProduct(undefined);
    cancel();
  };

  const {
    isOpen: isSuccessFlyoutOpen,
    open: openSuccessFlyout,
    cancel: closeSuccessFlyout,
  } = useSuccessFlyoutOpenedState();

  const selectedProductTrackingData = selectedProduct
    ? {
        recoType: trackingData?.recoType,
        elements: [{type: 'product' as const, id: selectedProduct.product.id}],
      }
    : undefined;

  return (
    <>
      <Flyout
        onClose={closeProductOutfitFlyout}
        open={isOpen}
        data-testid={dataTestId}
        headerContent={
          <Header>
            <ProductImageWrapper>
              <ProductImage
                src={headerContent.image.src}
                alt={headerContent.image.description}
                padded={headerContent.image.isEnclosed}
                increasePadding
              />
            </ProductImageWrapper>
            <div>
              <HeaderSubtitle>{headerContent.subline}</HeaderSubtitle>
              <HeaderHeadline>{headerContent.headline}</HeaderHeadline>
            </div>
          </Header>
        }
        bodyContent={
          <Content>
            <Headline>{headline}</Headline>
            <FluidGrid>
              {products.map((tile, index) => (
                <FullHeightProductTile
                  key={tile.productId}
                  tile={tile}
                  streamPosition={index}
                  trackingData={trackingData}
                  addCtaLabel={t('Add')}
                  onClickAddCta={(payload: AddToBasketPayload) => {
                    const matchingTile = products.find(
                      p => p.productId === payload.product.id,
                    );
                    setSelectedProduct({
                      product: payload.product,
                      brandName: matchingTile?.brandName ?? '',
                    });
                    setSelectedCouponPromotionId(
                      matchingTile?.couponPromotionId,
                    );
                    setProductId(payload.product.id);
                    trackProductClick?.();
                    openSizeSelection();
                  }}
                />
              ))}
            </FluidGrid>
          </Content>
        }
      />

      {productId && (
        <AddToBasketSuccessFlyoutWithRecos
          productId={productId}
          onClose={() => {
            closeSuccessFlyout();
          }}
          show={isSuccessFlyoutOpen}
          sizeSelectionKey={sizeSelectionOpenedStateKey}
          statusSuccessSections={statusSuccessSections}
          freeDeliverySection={freeDeliverySection}
          selectedSize={undefined}
        />
      )}

      {selectedProduct && (
        <AddToBasketFlyout
          isOpen={addToBasket}
          product={selectedProduct.product}
          withSuccessToast={false}
          withErrorToast
          trackingData={{
            ...selectedProductTrackingData,
            brandName: selectedProduct.brandName,
          }}
          onSuccess={({freeDeliverySection, statusSuccessSections}) => {
            // We need to unset the selected product when the flyout is closed so that the state can be correctly updated if the same sized product is added again

            openSuccessFlyout();
            setSelectedProduct(undefined);
            setFreeDeliverySection(freeDeliverySection);
            setStatusSuccessSections(statusSuccessSections);
          }}
          onError={() => {
            // We need to unset the selected product when the flyout is closed so that the state can be correctly updated if the same sized product is added again
            setSelectedProduct(undefined);
          }}
          onClose={() => {
            cancelSizeSelection();
          }}
          onCaptchaClose={() => {
            // We need to unset the selected product when the flyout is closed so that the state can be correctly updated if the same sized product is added again
            setSelectedProduct(undefined);
          }}
          couponPromotionId={selectedCouponPromotionId}
          overrideBETrackingData={{
            linkedContentId: outfitPromotionId,
            linkedContentType: 'outfit',
          }}
          badgesListKeys={[]}
        />
      )}
    </>
  );
};

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  align-self: flex-start;
`;

const ProductImageWrapper = styled.div`
  width: 50px;
`;

const HeaderHeadline = styled(Text)`
  display: block;
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: ${FONT_SIZES.px20};
`;

const HeaderSubtitle = styled(Text)`
  display: block;
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: ${FONT_SIZES.px16};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.Grey1};
`;

const Headline = styled(Text)`
  display: block;
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: ${FONT_SIZES.px24};
`;

const Content = styled.div`
  overflow: auto;
`;
